import { useState, useEffect } from 'react'
import { fetchPodcasts } from 'util/podcast'
import { Combobox } from '@headlessui/react'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import { Podcast } from 'lib/types/generated-types'
import classNames from 'classnames'

export const ShowNameCombobox = ({ showName, setShowName, submissionId }) => {
  const [query, setQuery] = useState('')
  const [selectedShow, setSelectedShow] = useState(showName || null)
  const [podcasts, setPodcasts] = useState<Podcast[]>([])

  useEffect(() => {
    const getPodcasts = async () => {
      if (!submissionId) {
        return
      }
      const fetchedPodcasts = await fetchPodcasts()
      setPodcasts(fetchedPodcasts)
    }
    getPodcasts()
  }, [submissionId])

  if (!submissionId) {
    return null
  }

  function getUniqueShows(podcasts) {
    const unique = {}
    return podcasts.filter((podcast) => {
      if (unique[podcast.show_name]) return false
      unique[podcast.show_name] = true
      return true
    })
  }

  const filteredPodcasts =
    query === ''
      ? podcasts
      : podcasts.filter((podcast) =>
          podcast?.show_name?.toLowerCase().includes(query.toLowerCase())
        )

  const uniqueFilteredPodcasts = getUniqueShows(filteredPodcasts)

  return (
    <Combobox
      as="div"
      value={selectedShow}
      onChange={(value) => {
        // If the selected value is an object, it's from the podcasts list.
        if (typeof value === 'object') {
          setSelectedShow(value)
          setShowName(value?.show_name)
        } else {
          // If it's a string, it's a custom input.
          setSelectedShow(value)
          setShowName(value)
        }
      }}
    >
      <Combobox.Label className="block text-sm font-medium leading-6 text-gray-900">
        Show Name
      </Combobox.Label>
      <div className="relative mt-2">
        <Combobox.Input
          className="w-full rounded-md border-0 bg-white py-1.5 pl-3 pr-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
          onChange={(event) => {
            setQuery(event.target.value)
            setSelectedShow(event.target.value) // Set custom input as selected show.
            setShowName(event.target.value) // Also set the custom input as show name.
          }}
          displayValue={(value) => {
            // If value is an object, it's a podcast. Otherwise, it's a custom string.
            return typeof value === 'object' ? (value as any)?.show_name : value
          }}
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
          <ChevronUpDownIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </Combobox.Button>

        {uniqueFilteredPodcasts.length > 0 && (
          <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {uniqueFilteredPodcasts.map((podcast) => (
              <Combobox.Option
                key={podcast.id}
                value={podcast}
                className={({ active }) =>
                  classNames(
                    'relative cursor-default select-none py-2 pl-3 pr-9',
                    active ? 'bg-blue-600 text-white' : 'text-gray-900'
                  )
                }
              >
                {({ active, selected }) => (
                  <>
                    <span
                      className={classNames(
                        'block truncate',
                        selected && 'font-semibold'
                      )}
                    >
                      {podcast.show_name}
                    </span>

                    {selected && (
                      <span
                        className={classNames(
                          'absolute inset-y-0 right-0 flex items-center pr-4',
                          active ? 'text-white' : 'text-blue-600'
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  )
}
